import { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getAccountNavigationItems } from 'v2/layouts/AccountWorkspaceLayout/constants';
import { ORGANIZATION_NAVIGATION_ITEMS } from 'v2/layouts/OrganizationWorkspaceLayout/constants';

import type { AuthorizationParams } from '@auth0/auth0-react';

import AccountDetails from 'components/AccountDetails';
import AuthChecker from 'components/AuthChecker';
import AuthenticationProvider from 'components/AuthenticationProvider';
import ChatSupport from 'components/ChatSupport';
import EquipmentDetails from 'components/EquipmentDetails';
import KmlImportForm from 'components/KmlImport';
import OrganizationDetails from 'components/OrganizationDetails';
import OrganizationSubscriptionDetails from 'components/OrganizationSubscriptionDetails';
import TeamDetails from 'components/TeamDetails';
import AircraftList from 'components/lists/AircraftList';
import ControllerList from 'components/lists/ControllerList';

import useCurrentOrganization from 'hooks/useCurrentOrganization';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useVersionPreference from 'hooks/useVersionPreference';
import { VERSION_PREFERENCE } from 'hooks/useVersionPreference/constants';

import FullScreenLayout from 'layouts/FullScreenLayout';
import Layout from 'layouts/Layout';
import SimpleLayout from 'layouts/SimpleLayout';
import WideLayout from 'layouts/WideLayout';

const AccountDrawerPage = lazy(() => import('pages/AccountDrawerPage'));
const AccountPage = lazy(() => import('pages/AccountPage'));
const AsAppliedMapResultsPage = lazy(() => import('pages/AsAppliedMapResultsPage'));
const CertificatesAndExemptionsPage = lazy(() => import('pages/CertificatesAndExemptionsPage'));
const CreateCustomerPage = lazy(() => import('pages/CreateCustomerPage'));
const CreateFaaReportPage = lazy(() => import('pages/CreateFaaReportPage'));
const CreateFlightGroupPage = lazy(() => import('pages/CreateFlightGroupPage'));
const CreateInvoicePage = lazy(() => import('pages/CreateInvoicePage'));
const CustomersPage = lazy(() => import('pages/CustomersPage'));
const DdmRedirectPage = lazy(() => import('pages/DdmRedirectPage'));
const EaVisionRedirectPage = lazy(() => import('pages/EaVisionRedirectPage'));
const EditAircraftPage = lazy(() => import('pages/EditAircraftPage'));
const EditCompanyDetailsPage = lazy(() => import('pages/EditCompanyDetailsPage'));
const EditControllerPage = lazy(() => import('pages/EditControllerPage'));
const EditPersonOfContactPage = lazy(() => import('pages/EditPersonOfContactPage'));
const EditUserDetailsPage = lazy(() => import('pages/EditUserDetailsPage'));
const FaaReportsPage = lazy(() => import('pages/FaaReportsPage'));
const FieldViewRedirectPage = lazy(() => import('pages/FieldViewRedirectPage'));
const FlightGroupAddFlightPage = lazy(() => import('pages/FlightGroupAddFlightPage'));
const FlightGroupFlightUploadPage = lazy(() => import('pages/FlightGroupFlightUploadPage'));
const FlightGroupPage = lazy(() => import('pages/FlightGroupPage'));
const FlightLogPage = lazy(() => import('pages/FlightLogPage'));
const FlightPage = lazy(() => import('pages/FlightPage'));
const GenerateAsAppliedMapPage = lazy(() => import('pages/GenerateAsAppliedMapPage'));
const HomePage = lazy(() => import('pages/HomePage'));
const InvoicePage = lazy(() => import('pages/InvoicePreviewPage'));
const JohnDeereRedirectPage = lazy(() => import('pages/JohnDeereRedirectPage'));
const LoginPage = lazy(() => import('pages/LoginPage'));
const LogoutPage = lazy(() => import('pages/LogoutPage'));
const ManageConnectionsPage = lazy(() => import('pages/ManageConnectionsPage'));
const ManageOrganizationPage = lazy(() => import('pages/ManageOrganizationPage'));
const MapboxTestPage = lazy(() => import('pages/MapboxTestPage'));
const NewAircraftPage = lazy(() => import('pages/NewAircraftPage'));
const NewControllerPage = lazy(() => import('pages/NewControllerPage'));
const NewProductUsageReportPage = lazy(() => import('pages/NewProductUsageReportPage'));
const OperatorTileOverviewPage = lazy(() => import('pages/OperatorTileOverviewPage'));
const OperatorTilePage = lazy(() => import('pages/OperatorTilePage'));
const ProductUsageReportListPage = lazy(() => import('pages/ProductUsageReportListPage'));
const ProductUsageReportPage = lazy(() => import('pages/ProductUsageReportPage'));
const QuickbooksRedirectPage = lazy(() => import('pages/QuickbooksRedirectPage'));
const SendInvoicePage = lazy(() => import('pages/SendInvoicePage'));
const SignupPage = lazy(() => import('pages/SignupPage'));
const StripeConfirmationRedirectPage = lazy(() => import('pages/StripeConfirmationRedirectPage'));
const TermsAndConditionsPage = lazy(() => import('pages/TermsAndConditionsPage'));
const UploadFlightLogPage = lazy(() => import('pages/UploadFlightLogPage'));
const VerifyAccountPage = lazy(() => import('pages/VerifyAccountPage'));
const VerifyEmailPage = lazy(() => import('pages/VerifyEmailPage'));
const VerifyOrganizationPage = lazy(() => import('pages/VerifyOrganizationPage'));
const WorkOrderPage = lazy(() => import('pages/WorkOrderPage'));
const WorkOrderReportPage = lazy(() => import('pages/WorkOrderReportPage'));
const BadPathErrorPage = lazy(() => import('pages/errors/BadPathErrorPage'));
const DashboardPage = lazy(() => import('v2/pages/DashboardPage'));
const V2CreateFaaReportPage = lazy(() => import('v2/pages/CreateFaaReportPage'));
const FlightsAndMapsPage = lazy(() => import('v2/pages/FlightsAndMapsPage'));
const JobsPage = lazy(() => import('v2/pages/JobsPage'));
const MapWorkspaceLayout = lazy(() => import('v2/layouts/MapWorkspaceLayout'));
const NavigationLayout = lazy(() => import('v2/layouts/NavigationLayout'));
const AccountWorkspaceLayout = lazy(() => import('v2/layouts/AccountWorkspaceLayout'));
const OrganizationWorkspaceLayout = lazy(() => import('v2/layouts/OrganizationWorkspaceLayout'));
const V2EditApplicatorCertificationPage = lazy(
    () => import('v2/pages/EditApplicatorCertificationPage')
);
const V2EditUserDetailsPage = lazy(() => import('v2/pages/EditUserDetailsPage'));
const V2EditAircraftPage = lazy(() => import('v2/pages/EditAircraftPage'));
const V2EditCertificationsPage = lazy(() => import('v2/pages/EditCertificationsPage'));
const V2EditControllerPage = lazy(() => import('v2/pages/EditControllerPage'));
const V2EditPesticideLicensePage = lazy(() => import('v2/pages/EditPesticideLicensePage'));
const V2EditCompanyDetailsPage = lazy(() => import('v2/pages/EditCompanyDetailsPage'));
const V2EditPersonOfContactPage = lazy(() => import('v2/pages/EditPersonOfContactPage'));
const V2InviteUserPage = lazy(() => import('v2/pages/InviteUserPage'));
const V2NewApplicatorCertificationPage = lazy(
    () => import('v2/pages/NewApplicatorCertificationPage')
);
const V2OrganizationMemberPage = lazy(() => import('v2/pages/OrganizationMemberPage'));
const V2NewAircraftPage = lazy(() => import('v2/pages/NewAircraftPage'));
const V2NewControllerPage = lazy(() => import('v2/pages/NewControllerPage'));
const V2NewPesticideLicensePage = lazy(() => import('v2/pages/NewPesticideLicensePage'));
const ProductUsageReportsPage = lazy(() => import('v2/pages/ProductUsageReportsPage'));
const V2SitesPage = lazy(() => import('v2/pages/SitesPage'));
const WorkOrdersPage = lazy(() => import('v2/pages/WorkOrdersPage'));

const AppRoutes = () => {
    const [currentLayout, setCurrentLayout] = useState<string>('narrow');

    const handleLayoutSwitch = useCallback((layoutType: string) => {
        setCurrentLayout(layoutType);
    }, []);

    const { currentOrganization } = useCurrentOrganization();
    const {
        ACCOUNT_DRAWER_LAYOUT,
        CUSTOMER_INVOICING,
        CUSTOMERS,
        FAA_REPORTS,
        MANAGE_CONNECTIONS,
        OPERATOR_MAP_TILE,
        STATE_REPORTING,
        V2_RESKIN,
        V2_RESKIN_CUTOFF
    } = useFeatureFlags();
    const { preference: versionPreference } = useVersionPreference();
    const shouldRedirectToV2 =
        V2_RESKIN && (V2_RESKIN_CUTOFF || versionPreference === VERSION_PREFERENCE.V2);

    const authParams = useRef<AuthorizationParams>({
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        redirect_uri: window.location.origin
    });

    useEffect(() => {
        if (currentOrganization) {
            authParams.current = {
                ...authParams.current,
                organization: currentOrganization.auth0Id
            };
        }
    }, [currentOrganization]);

    return (
        <Routes>
            <Route
                element={
                    <AuthenticationProvider
                        authorizationParams={authParams.current}
                        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
                        domain={import.meta.env.VITE_AUTH0_DOMAIN}
                    />
                }
                path="/"
            >
                <Route element={<LoginPage />} path="account/login" />

                <Route element={<LogoutPage />} path="account/logout" />

                <Route element={<SignupPage />} path="signup" />

                <Route element={<SignupPage />} path="signup/:tag" />

                <Route element={<ChatSupport />} path="/">
                    <Route element={<AuthChecker />} path="/">
                        <Route element={<FullScreenLayout />}>
                            {OPERATOR_MAP_TILE && (
                                <>
                                    <Route element={<OperatorTilePage />} path="/provider-map" />

                                    <Route
                                        element={<OperatorTileOverviewPage />}
                                        path="/provider-map/:id"
                                    />

                                    <Route
                                        element={<OperatorTilePage />}
                                        path="/provider-map/:id/edit"
                                    />
                                </>
                            )}

                            {CUSTOMERS && (
                                <>
                                    <Route element={<CustomersPage />} path="/customers" />

                                    <Route
                                        element={<CreateCustomerPage />}
                                        path="/create-customer"
                                    />

                                    <Route
                                        element={<CreateCustomerPage />}
                                        path="/customers/:id/edit"
                                    />
                                </>
                            )}

                            {ACCOUNT_DRAWER_LAYOUT && !V2_RESKIN && (
                                <Route>
                                    <Route
                                        element={<AccountDrawerPage />}
                                        path={'account/:childroute'}
                                    />
                                </Route>
                            )}

                            <Route element={<FlightGroupPage />} path="/flight-group/:id" />

                            <Route
                                element={<FlightGroupAddFlightPage />}
                                path="/flight-group/:id/flights"
                            />

                            <Route
                                element={<FlightGroupFlightUploadPage />}
                                path="/flight-group/:id/flights/upload"
                            />
                        </Route>

                        <Route element={<SimpleLayout />} path="verify">
                            <Route element={<VerifyEmailPage />} path="email" />

                            <Route element={<VerifyAccountPage />} path="account" />

                            <Route path="organization">
                                <Route element={<VerifyOrganizationPage />} index />
                            </Route>
                        </Route>

                        <Route element={<Layout type={currentLayout} />} path="/">
                            <Route
                                element={
                                    <GenerateAsAppliedMapPage onLayoutSwitch={handleLayoutSwitch} />
                                }
                                path="generate"
                            />

                            <Route element={<KmlImportForm />} path="kml-import" />

                            {!ACCOUNT_DRAWER_LAYOUT && (
                                <Route element={<AccountPage />} path="account">
                                    <Route element={<Navigate replace to="details" />} index />

                                    <Route element={<AccountDetails />} path="details" />

                                    <Route element={<></>} path="settings" />

                                    <Route path="edit">
                                        <Route element={<EditUserDetailsPage />} path="user" />
                                    </Route>
                                </Route>
                            )}

                            <Route path="aam">
                                <Route element={<Navigate to="/generate" />} index />

                                <Route
                                    element={
                                        <AsAppliedMapResultsPage
                                            onLayoutSwitch={handleLayoutSwitch}
                                        />
                                    }
                                    path=":jobId/:mapType"
                                />
                            </Route>

                            <Route element={<UploadFlightLogPage />} path="upload-flight-log" />

                            <Route element={<FlightPage />} path="flight-logs/:flightId" />

                            <Route element={<MapboxTestPage />} path="mapbox-test" />

                            {!V2_RESKIN && (
                                <>
                                    <Route element={<ManageOrganizationPage />} path="organization">
                                        <Route element={<Navigate replace to="details" />} index />

                                        <Route element={<OrganizationDetails />} path="details" />

                                        <Route
                                            element={<OrganizationSubscriptionDetails />}
                                            path="subscription"
                                        />

                                        <Route element={<TeamDetails />} path="team">
                                            <Route
                                                element={<Navigate replace to="active" />}
                                                index
                                            />

                                            <Route element={<TeamDetails />} path="active" />

                                            <Route element={<TeamDetails />} path="disabled" />

                                            <Route element={<TeamDetails />} path="invited" />
                                        </Route>

                                        <Route element={<EquipmentDetails />} path="equipment">
                                            <Route
                                                element={<Navigate replace to="aircraft" />}
                                                index
                                            />

                                            <Route element={<AircraftList />} path="aircraft" />

                                            <Route element={<ControllerList />} path="controller" />
                                        </Route>
                                    </Route>

                                    <Route path="organization">
                                        <Route path="edit">
                                            <Route
                                                element={<EditAircraftPage />}
                                                path="aircraft/:aircraftId"
                                            />

                                            <Route
                                                element={<EditCompanyDetailsPage />}
                                                path="company"
                                            />

                                            <Route
                                                element={<EditPersonOfContactPage />}
                                                path="contact"
                                            />

                                            <Route
                                                element={<CertificatesAndExemptionsPage />}
                                                path="certifications"
                                            />

                                            <Route
                                                element={<EditControllerPage />}
                                                path="controller/:controllerId"
                                            />
                                        </Route>

                                        <Route path="new">
                                            <Route element={<NewAircraftPage />} path="aircraft" />

                                            <Route
                                                element={<NewControllerPage />}
                                                path="controller"
                                            />
                                        </Route>
                                    </Route>
                                </>
                            )}

                            <Route path="work-order">
                                <Route element={<WorkOrderPage />} index />

                                <Route path=":workOrderId">
                                    <Route element={<WorkOrderReportPage />} index />

                                    <Route
                                        element={<WorkOrderReportPage startEditable />}
                                        path="edit"
                                    />

                                    <Route
                                        element={<WorkOrderReportPage isDuplicate startEditable />}
                                        path="duplicate"
                                    />

                                    <Route
                                        element={<NewProductUsageReportPage />}
                                        path="product-usage-report/:draftId?"
                                    />
                                </Route>
                            </Route>

                            <Route path="product-usage-report">
                                <Route path=":productUsageReportId">
                                    <Route element={<ProductUsageReportPage />} index />

                                    <Route
                                        element={<NewProductUsageReportPage isEditMode={true} />}
                                        path="edit"
                                    />
                                </Route>
                            </Route>

                            <Route element={<BadPathErrorPage />} path="/*" />
                        </Route>

                        <Route element={<WideLayout />}>
                            <Route
                                element={
                                    shouldRedirectToV2 ? (
                                        <Navigate replace to="/v2" />
                                    ) : (
                                        <HomePage />
                                    )
                                }
                                index
                            />

                            {MANAGE_CONNECTIONS && (
                                <Route
                                    element={<ManageConnectionsPage />}
                                    path="connections/:code?/:source?"
                                />
                            )}

                            <Route element={<FlightLogPage />} path="flight-logs" />

                            {FAA_REPORTS && (
                                <Route path="faa-reports">
                                    <Route element={<FaaReportsPage />} index />

                                    <Route
                                        element={<CreateFaaReportPage isEditable={false} />}
                                        path="view/:faaReportId"
                                    />

                                    <Route
                                        element={<CreateFaaReportPage isEditable={true} />}
                                        path="create/:faaReportId"
                                    />
                                </Route>
                            )}

                            <Route path="product-usage-report">
                                <Route element={<ProductUsageReportListPage />} index />

                                <Route
                                    element={<ProductUsageReportListPage newPur={true} />}
                                    path="newPur"
                                />
                            </Route>

                            {CUSTOMER_INVOICING && (
                                <>
                                    <Route element={<CreateInvoicePage />} path="/invoice" />

                                    <Route
                                        element={<CreateInvoicePage />}
                                        path="/invoice/:invoiceId/edit"
                                    />

                                    <Route element={<InvoicePage />} path="/invoice/:invoiceId" />

                                    <Route
                                        element={<SendInvoicePage />}
                                        path="/invoice/send/:invoiceId"
                                    />
                                </>
                            )}

                            <Route path="flight-group">
                                <Route element={<CreateFlightGroupPage />} path="create" />

                                <Route element={<CreateFlightGroupPage />} path=":id/edit" />
                            </Route>
                        </Route>

                        {V2_RESKIN && (
                            <Route
                                element={
                                    <NavigationLayout
                                        navigationItems={getAccountNavigationItems(STATE_REPORTING)}
                                    />
                                }
                                path="account"
                            >
                                <Route element={<AccountWorkspaceLayout />}>
                                    <Route element={<Navigate replace to="myaccount" />} index />

                                    <Route path="myaccount" />

                                    <Route path="certifications" />

                                    <Route
                                        element={
                                            <Navigate replace to="/organization/subscription" />
                                        }
                                        path="subscriptions"
                                    />

                                    <Route
                                        element={<Navigate replace to="/organization/users" />}
                                        path="users"
                                    />

                                    <Route
                                        element={
                                            <Navigate replace to="/organization/invitations" />
                                        }
                                        path="invitations"
                                    />

                                    <Route path="edit">
                                        <Route element={<V2EditUserDetailsPage />} path="user" />

                                        <Route
                                            element={<V2EditApplicatorCertificationPage />}
                                            path="applicator-certification/:certificationId"
                                        />
                                    </Route>

                                    <Route path="new">
                                        <Route
                                            element={
                                                <Navigate
                                                    replace
                                                    to="/organization/new/invitation"
                                                />
                                            }
                                            path="invitation"
                                        />

                                        <Route
                                            element={<V2NewApplicatorCertificationPage />}
                                            path="applicator-certification"
                                        />
                                    </Route>
                                </Route>
                            </Route>
                        )}

                        {V2_RESKIN && (
                            <Route
                                element={
                                    <NavigationLayout
                                        navigationItems={ORGANIZATION_NAVIGATION_ITEMS}
                                    />
                                }
                                path="organization"
                            >
                                <Route element={<OrganizationWorkspaceLayout />}>
                                    {/* Section routes carry no element: the workspace renders the
                                        section column from the path and reserves its outlet for
                                        the edit panel. */}
                                    <Route element={<Navigate replace to="details" />} index />

                                    <Route path="details" />

                                    <Route path="subscription" />

                                    <Route path="users" />

                                    {/* The member opens in the workspace edit panel over
                                        the users list, like the other edit flows here. */}
                                    <Route
                                        element={<V2OrganizationMemberPage />}
                                        path="users/:userId"
                                    />

                                    <Route path="invitations" />

                                    <Route
                                        element={<Navigate replace to="/organization/users" />}
                                        path="team/*"
                                    />

                                    <Route path="equipment">
                                        <Route element={<Navigate replace to="aircraft" />} index />

                                        <Route path="aircraft" />

                                        <Route path="controller" />
                                    </Route>

                                    <Route path="edit">
                                        <Route
                                            element={<V2EditAircraftPage />}
                                            path="aircraft/:aircraftId"
                                        />

                                        <Route
                                            element={<V2EditCompanyDetailsPage />}
                                            path="company"
                                        />

                                        <Route
                                            element={<V2EditPersonOfContactPage />}
                                            path="contact"
                                        />

                                        <Route
                                            element={<V2EditCertificationsPage />}
                                            path="certifications"
                                        />

                                        <Route
                                            element={<V2EditControllerPage />}
                                            path="controller/:controllerId"
                                        />

                                        <Route
                                            element={<V2EditPesticideLicensePage />}
                                            path="pesticide-license/:licenseId"
                                        />

                                        {STATE_REPORTING && (
                                            <Route
                                                element={<V2EditApplicatorCertificationPage />}
                                                path="user/:userId/applicator-certification/:certificationId"
                                            />
                                        )}
                                    </Route>

                                    <Route path="new">
                                        <Route element={<V2NewAircraftPage />} path="aircraft" />

                                        <Route
                                            element={<V2NewControllerPage />}
                                            path="controller"
                                        />

                                        <Route element={<V2InviteUserPage />} path="invitation" />

                                        <Route
                                            element={<V2NewPesticideLicensePage />}
                                            path="pesticide-license"
                                        />

                                        {STATE_REPORTING && (
                                            <Route
                                                element={<V2NewApplicatorCertificationPage />}
                                                path="user/:userId/applicator-certification"
                                            />
                                        )}
                                    </Route>
                                </Route>
                            </Route>
                        )}

                        {V2_RESKIN && (
                            <Route element={<NavigationLayout />} path="v2">
                                <Route element={<DashboardPage />} index />

                                <Route element={<V2CreateFaaReportPage />} path="faa-reports" />

                                <Route element={<FlightsAndMapsPage />} path="flights-and-maps" />

                                <Route element={<MapWorkspaceLayout />}>
                                    <Route element={<JobsPage />} path="jobs" />

                                    <Route
                                        element={<ProductUsageReportsPage />}
                                        path="product-usage-reports"
                                    />

                                    <Route element={<V2SitesPage />} path="sites" />

                                    <Route element={<WorkOrdersPage />} path="work-orders" />
                                </Route>
                            </Route>
                        )}
                    </Route>
                </Route>

                <Route element={<SimpleLayout />} path="/">
                    <Route element={<TermsAndConditionsPage />} path="terms-of-service" />
                </Route>
            </Route>

            <Route path="auth">
                <Route element={<DdmRedirectPage />} path="ddm/callback" />

                <Route element={<EaVisionRedirectPage />} path="eavision/callback" />

                <Route element={<JohnDeereRedirectPage />} path="johndeere/callback" />

                <Route element={<FieldViewRedirectPage />} path="fieldview/callback" />

                <Route element={<QuickbooksRedirectPage />} path="quickbooks/callback" />

                <Route element={<StripeConfirmationRedirectPage />} path="stripe/callback" />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
